import type { ICountry, IProvince } from '@/entities/country'
import type { LocationStatusFilter } from '@/shared/constants/location'
import { useMemo, useState } from 'react'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import { filterLocations } from '@/features/location'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { LOCATION_STATUS_FILTER } from '@/shared/constants/location'

interface UseLocationPageResult {
  countriesLoading: boolean
  provincesLoading: boolean
  countries: ICountry[]
  selectedCountry: ICountry | undefined
  provinces: IProvince[]
  provinceTotal: number
  provincePage: number
  provincePageSize: number
  keyword: string
  status: LocationStatusFilter
  selectCountry: (country: ICountry) => void
  changeKeyword: (value: string) => void
  changeStatus: (value: LocationStatusFilter) => void
  changeProvincePage: (page: number, pageSize: number) => void
}

/**
 * State for the location management page: country list, selected country and its provinces.
 * Search/status filters apply to the province table; pagination is client-side because
 * master-service returns the whole province list of a country in one call.
 */
export function useLocationPage(): UseLocationPageResult {
  const [selectedId, setSelectedId] = useState('')
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<LocationStatusFilter>(LOCATION_STATUS_FILTER.ALL)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const countriesQuery = useCountriesQuery({})
  const countries = useMemo(() => countriesQuery.data?.data ?? [], [countriesQuery.data])

  // * VI: Mặc định chọn quốc gia đầu tiên để bảng tỉnh thành luôn có dữ liệu | EN: Default to the first country so the province table is never empty on load
  const selectedCountry = countries.find((item) => item.id === selectedId) ?? countries[0]

  const provincesQuery = useProvincesQuery({ countryId: selectedCountry?.id ?? '' })
  const allProvinces = useMemo(() => provincesQuery.data?.data ?? [], [provincesQuery.data])
  const filtered = useMemo(
    () => filterLocations(allProvinces, keyword, status),
    [allProvinces, keyword, status],
  )
  const provinces = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  )

  return {
    countriesLoading: countriesQuery.isLoading,
    provincesLoading: provincesQuery.isLoading,
    countries,
    selectedCountry,
    provinces,
    provinceTotal: filtered.length,
    provincePage: page,
    provincePageSize: pageSize,
    keyword,
    status,
    selectCountry: (country) => {
      setSelectedId(country.id)
      setPage(1)
    },
    changeKeyword: (value) => {
      setKeyword(value)
      setPage(1)
    },
    changeStatus: (value) => {
      setStatus(value)
      setPage(1)
    },
    changeProvincePage: (nextPage, nextPageSize) => {
      setPage(nextPage)
      setPageSize(nextPageSize)
    },
  }
}
